import React, { useEffect, useState } from "react";
import styles from "./prescription-listing.module.scss";
import { Box, Container, Stack } from "@mui/system";
import { Button, IconButton, Card, CssBaseline, List, ListItem, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Link as MuiLink } from "@mui/material";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import FilterButton from "../../../components/FilterButton/FilterButton";

import { useTranslation } from "react-i18next";
import { HTTPMethod, callApi as PrescriptionListingApiCall } from "../../../services/HttpService/HttpService";
import { PrescriptionApi } from "../../../constants/ApiConstants";
import { FileType, FileUploadComponent, StatusFileUpload } from "../../../common/enums";
import AddPrescription from "../add-prescription/add-prescription";
import NoData from "../../../components/noData/noData";
import Panel from "../../../components/Panel/Panel";
import ItemLabel from "../../../components/ItemLabel/ItemLabel";
import Icon from "../../../components/Icon/Icon";
import StatusBadge from "../../../components/StatusBadge/StatusBadge";
import EditPrescription from "../edit-prescription/edit-prescription";
import CommonAlert from "../../../components/common-alert/common-alert";
import PrescriptionListingActions from "../prescription-listing-actions/prescription-listing-actions";
import PrescriptionListSkeleton from "./PrescriptionListSkeleton";
import { successToast } from "../../../components/ToastMessage/ToastMessage";
import { LineOfBusinessEnum } from '../../../constants/BusinessConstants';

function formatExpirationDate(dateTimeString) {
  const dateObject = new Date(dateTimeString);
  const formattedDate = dateObject.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  return formattedDate;
}

const hasChecklistFlag = (value) => {
  switch (typeof value) {
    case 'boolean':
      return value;
    case 'number':
      return value > 0;
    case 'string':
      return ['true', '1'].includes(value.trim().toLowerCase());
    default:
      return false;
  }
};

export const StatusName = ({ item }) => {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} sx={{ mb: 1, gap: 2, alignItems: { xs: "flex-start", sm: "center" } }}>
      <Typography variant="h6">{item?.display_name?.replace(/patient/gi, LineOfBusinessEnum.patientToMember)}</Typography>
      {item?.status_name !== null && (
        <StatusBadge status={StatusFileUpload[item?.status_name]} />
      )}
    </Stack>
  );
}
export const DeleteDocumentDialog = ({ isOpen, handleCancel, boldText, handleSubmit }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.CommonAlert} data-testid="CommonAlert">
      <Dialog open={isOpen} onClose={handleCancel}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "400px",
            },
          },
        }}
      >
        <DialogTitle className={styles.commonDialogTitle}>
          <span className={styles.commonFexContainer}>{t("Prescriptions.removeDocument")}</span>
        </DialogTitle>
        <DialogContent style={{ padding: '15px 25px' }}>
          <p className={styles.dialogDescription}>
            <span className="displayFlex">{t("Prescriptions.deleteDocumentContent")}</span>
            {boldText && <span className={styles.dialogBold}>{boldText}</span>}?
          </p>
        </DialogContent>
        <DialogActions className='alertButton'>
          <Button
            onClick={handleCancel}
            data-testid="cancel"
            color='secondary' size="small">
            {t("Prescriptions.cancel")}
          </Button>
          <Button
            data-testid='submitButton'
            onClick={handleSubmit}
            color='error' size="small">
            {t("Prescriptions.proceed")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export const DocumentListing = ({ fileSetting, componentId, component = 1 }) => {
  const { t } = useTranslation();
  const [documentListData, setDocumentListData] = useState([]);
  const [fileDeleted, setFileDeleted] = useState(null);
  const [deletedFileName, setDeletedFileName] = useState('');
  const [documentList, setDocumentList] = useState(null)
  const [safetyAssessmentData, setSafetyAssessmentData] = useState([]);
  const [safeHomeOxygenTherapyData, setSafeHomeOxygenTherapyData] = useState([]);
  const [patientAcknowledgementData, setPatientAcknowledgementData] = useState([]);

  useEffect(() => {
    if (componentId) {
      handleDocumentListing();
    }
  }, [componentId]);

  const handleDocumentListing = async () => {
    const requestBody = {
      order_id: component == FileUploadComponent.EditOrder ? componentId : null,
      patient_id: component == FileUploadComponent.Ordering ? componentId : null
    };
    const response = await PrescriptionListingApiCall(
      HTTPMethod.Post,
      PrescriptionApi.prescriptionListing,
      requestBody
    );
    if (response && response.status_code == 200 && response.data) {
      setDocumentListData(response?.data?.files.filter((file) => file.is_esa === 0 && file.file_type !== 5 && file.file_type !== 6));
      setSafetyAssessmentData(response?.data?.files?.filter((file) => file.is_esa === 1));
      setSafeHomeOxygenTherapyData(response?.data?.files?.filter((file) => file.file_type === 5));
      setPatientAcknowledgementData(response?.data?.files?.filter((file) => file.file_type === 6));
    } else {
      setDocumentListData([]);
    }
  };

  const handlePdfApi = async (fileId, isViewPdf) => {
    if (fileId) {
      const pdfResponse = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", fileId));
      if (pdfResponse && pdfResponse.status_code == 200) {
        const pdfUrl = pdfResponse.data.url;
        if (isViewPdf) {
          const link = document.createElement("a");
          link.target = "_blank";
          link.href = pdfUrl;
          link.click();
        } else {
          fetch(pdfUrl)
            .then((response) => response.blob())
            .then((blob) => {
              const url = window.URL.createObjectURL(new Blob([blob]));
              const a = document.createElement('a');
              a.href = url;
              a.download = pdfResponse.data.file_name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            })
            .catch((error) => {
              console.error('Error downloading file:', error);
            });
        }

      }
    }
  }

  const handleEditFile = (item) => {
    setDocumentList(item);
  }

  const handleFileDelete = async (e) => {
    e.preventDefault();
    await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.removeFile.replace("{file_upload_id}", fileDeleted?.file_id));
    setFileDeleted(null);
    handleDocumentListing();
    successToast(t("Prescriptions.documentRemovedSuccessfully"));
  }

  const documentLength = documentListData?.length ?? 1;

  return (
    <Box width={1}>
      {documentLength > 0 &&
        <List disablePadding sx={{ width: "100%" }}>
          {documentListData.map((item, index) => {
            const quotefileStoredName = item?.stored_name?.includes("PACE/Quotes");
            return (
              (((fileSetting || (!fileSetting && !item.is_o2_prescription)) && (item?.status !== 4 || component !== FileUploadComponent.Order)) &&
                <ListItem key={item.file_id} divider disablePadding sx={{ py: 4 }}>
                  <Stack sx={{ width: '100%' }} spacing={2}>
                    <StatusName item={item} />
                    <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }}>
                      <Stack spacing={1}>
                        {item?.order_id && item?.is_o2_prescription && (
                          <ItemLabel label={t('Dashboard.orderID')}>
                            <MuiLink component={RouterLink} to={`/orders/order-details/${item?.order_id}`}>{item?.order_id}</MuiLink>
                          </ItemLabel>
                        )}
                        {item?.is_o2_prescription && <ItemLabel label={t('Prescriptions.fileType')} text={FileType[item?.is_o2_prescription]} />}
                        {item?.formatted_expiration_date && <ItemLabel label={t('Prescriptions.expiration')} text={formatExpirationDate(item?.formatted_expiration_date)} />}
                        {item?.rejection_reason && <ItemLabel label={t('Prescriptions.providerComment')} text={item?.rejection_reason} />}
                        {item?.formatted_created_date && <ItemLabel label={t('Prescriptions.uploadedOn')} text={item?.formatted_created_date} />}
                        {item?.created_by_user && <ItemLabel label={t('Prescriptions.uploadedBy')} text={item?.created_by_user} />}
                        {item?.formatted_last_updated_date && (item?.formatted_created_date !== item?.formatted_last_updated_date) && (
                          <ItemLabel label={t('Prescriptions.updatedOn')} text={item?.formatted_last_updated_date} />
                        )}
                        {item?.last_updated_by_user && (item?.formatted_created_date !== item?.formatted_last_updated_date) && (
                          <ItemLabel label={t('Prescriptions.updatedBy')} text={item?.last_updated_by_user} />
                        )}
                        {item?.formatted_rejected_on && <ItemLabel label={t('Prescriptions.rejectedOn')} text={item?.formatted_rejected_on} />}
                        {item?.rejected_by && <ItemLabel label={t('Prescriptions.rejectedBy')} text={item?.rejected_by} />}
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Tooltip title={<span>{t("Prescriptions.viewDocument")}</span>}>
                          <IconButton
                            color="success"
                            data-testid={"ViewDocument" + index}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePdfApi(item?.file_id, true);
                            }}>
                            <Icon icon="fa-eye" />
                          </IconButton>
                        </Tooltip>
                        {!quotefileStoredName && (
                          <Tooltip title={<span>{t("Prescriptions.editDocument")}</span>}>
                            <IconButton
                              color="secondary"
                              data-testid={"Edit" + index}
                              onClick={() => handleEditFile(item)}>
                              <Icon icon="fa-pencil" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={<span>{t("Prescriptions.downloadDocument")}</span>}>
                          <IconButton
                            color="primary"
                            data-testid={"DownloadDocument" + index}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePdfApi(item?.file_id, false);
                            }}>
                            <Icon icon="fa-arrow-down-to-bracket" />
                          </IconButton>
                        </Tooltip>
                        {!quotefileStoredName && (
                          <Tooltip title={<span>{t("Prescriptions.removeDocument")}</span>}>
                            <IconButton
                              color="error"
                              data-testid={"Delete" + index}
                              onClick={(e) => {
                                e.preventDefault();
                                setDeletedFileName((item?.display_name ?? ""));
                                setFileDeleted(item);
                              }}>
                              <Icon icon="fa-trash" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </ListItem>)
            );
          })}
          {safetyAssessmentData?.length > 0 &&
            <List disablePadding>
              {safetyAssessmentData?.map((item, index) => (
                <EnvironmentalSafetyAssessment
                  item={item}
                  index={index}
                  fileTypeData={safetyAssessmentData}
                  t={t}
                  key={item?.file_id}
                  noTopRadius />
              ))}
            </List>
          }
          {safeHomeOxygenTherapyData?.length > 0 && (
            <List disablePadding>
              {safeHomeOxygenTherapyData.map((item, index) => (
                <SafeHomeOxygenTherapy
                  item={item}
                  index={index}
                  fileTypeData={safeHomeOxygenTherapyData}
                  t={t}
                  key={item?.file_id}
                />
              ))}
            </List>
          )}

          {patientAcknowledgementData?.length > 0 && (
            <List disablePadding>
              {patientAcknowledgementData.map((item, index) => (
                <PatientAcknowledgement
                  item={item}
                  index={index}
                  fileTypeData={patientAcknowledgementData}
                  t={t}
                  key={item?.file_id}
                />
              ))}
            </List>
          )}
        </List>}

      <DeleteDocumentDialog
        handleCancel={() => { setFileDeleted(null); }}
        data-testid='DeleteDocumentDialog'
        handleSubmit={handleFileDelete}
        boldText={deletedFileName}
        isOpen={!!fileDeleted}
      />
      {documentList && <EditPrescription isEditDocumentOpen={!!documentList}
        currentListData={documentList} setIsEdited={handleDocumentListing} addPrescriptionSettings={fileSetting}
        handleClose={() => {
          setDocumentList(null);
        }} />}
    </Box>
  );
};
export const PatientDocumentListing = ({ fileSetting, componentId, component = 1 }) => {
  const { t } = useTranslation();
  const [documentListData, setDocumentListData] = useState([]);
  const [fileDeleted, setFileDeleted] = useState(null);
  const [deletedFileName, setDeletedFileName] = useState('');
  const [isEditDocumentOpen, setIsEditDocumentOpen] = useState(false)
  const [prescriptionFileId, setPrescriptionFileId] = useState('');
  const [isEdited, setIsEdited] = useState(false)
  const [safetyAssessmentData, setSafetyAssessmentData] = useState([]);
  const [safeHomeOxygenTherapyData, setSafeHomeOxygenTherapyData] = useState([]);
  const [patientAcknowledgementData, setPatientAcknowledgementData] = useState([]);
  
  useEffect(() => {
    if (componentId) {
      handleDocumentListing();
    }
  }, [componentId]);
  const handleDocumentListing = async () => {
    const requestBody = {
      order_id: null,
      patient_id: componentId
    };
    const response = await PrescriptionListingApiCall(
      HTTPMethod.Post,
      PrescriptionApi.prescriptionListing,
      requestBody
    );
    if (response && response.status_code == 200 && response.data) {
      setDocumentListData(response?.data?.files.filter((file) => file.is_esa === 0 && file.file_type !== 5 && file.file_type !== 6));
      setSafetyAssessmentData(response?.data?.files?.filter((file) => file.is_esa === 1));
      setSafeHomeOxygenTherapyData(response?.data?.files?.filter((file) => file.file_type === 5));
      setPatientAcknowledgementData(response?.data?.files?.filter((file) => file.file_type === 6));
    } else {
      setDocumentListData([]);
    }
  };
  const handlePdfView = async (item) => {
    const pdfAPIResponseView = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", item.file_id));
    if (pdfAPIResponseView && pdfAPIResponseView.status_code == 200) {
      const pdfUrl = pdfAPIResponseView.data.url;
      window.open(pdfUrl);
    }
  }
  const handleEditFile = (item) => {
    setPrescriptionFileId(item);
    setIsEditDocumentOpen(true);
  }

  const downloadFile = async (item) => {

    const pdfAPIResponseView = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", item.file_id));
    if (pdfAPIResponseView && pdfAPIResponseView.status_code == 200) {
      const downloadUrl = pdfAPIResponseView.data.url;

      fetch(downloadUrl)
        .then((response) => response.blob())
        .then((blob) => {

          const url = window.URL.createObjectURL(new Blob([blob]));
          const a = document.createElement('a');
          a.href = url;
          a.download = pdfAPIResponseView.data.file_name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch((error) => {
          console.error('Error downloading file:', error);
        });
    }

  };

  const handleFileRemove = async (e, item) => {
    e.preventDefault();
    const response = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.removeFile.replace("{file_upload_id}", fileDeleted?.file_id));

    if (response && response.status_code == 200 && response.data) {
      handleDocumentListing();
      successToast(t("Prescriptions.documentRemovedSuccessfully"));
    }
    setFileDeleted(null);
  }

  return (
    <>
      {documentListData?.length > 0 && (
        <Stack width={1}>
          <List disablePadding>
            {documentListData?.map((item, index) => (
              <DocumentListingData key={item?.file_id} item={item} index={index} fileTypeData={documentListData} t={t} handleEditFile={handleEditFile} handlePdfView={handlePdfView} downloadFile={downloadFile} handleFileRemove={handleFileRemove} setFileDeleted={setFileDeleted} setDeletedFileName={setDeletedFileName} safetyAssessmentData={safetyAssessmentData} />
            ))}
          </List>
        </Stack>
      )}
      {safetyAssessmentData?.length > 0 && (
        <Stack>
          <List disablePadding>
            {safetyAssessmentData?.map((item, index) => (
              <EnvironmentalSafetyAssessment
                item={item}
                index={index}
                fileTypeData={safetyAssessmentData}
                t={t}
                key={item?.file_id}
              />
            ))}
          </List>
        </Stack>
      )}
      {safeHomeOxygenTherapyData?.length > 0 && (
        <Stack>
          <List disablePadding>
            {safeHomeOxygenTherapyData?.map((item, index) => (
              <SafeHomeOxygenTherapy
                item={item}
                index={index}
                fileTypeData={safeHomeOxygenTherapyData}
                t={t}
                key={item?.file_id}
              />
            ))}
          </List>
        </Stack>
      )}
      {patientAcknowledgementData?.length > 0 && (
        <Stack>
          <List disablePadding>
            {patientAcknowledgementData?.map((item, index) => (
              <PatientAcknowledgement
                item={item}
                index={index}
                fileTypeData={patientAcknowledgementData}
                t={t}
                key={item?.file_id}
              />
            ))}
          </List>
        </Stack>
      )}
      {isEditDocumentOpen && <EditPrescription isEditDocumentOpen={isEditDocumentOpen}
        currentListData={prescriptionFileId} setIsEdited={setIsEdited} addPrescriptionSettings={fileSetting}
        handleClose={() => {
          setIsEditDocumentOpen(false);
          handleDocumentListing();
        }}> </EditPrescription>}
      <CommonAlert contentText={deletedFileName}
        headerText="Remove Document"
        okText="Proceed"
        cancelText="Cancel"
        isOpen={!!fileDeleted}
        handleSubmit={handleFileRemove}
        okButtonStyle={'red'}
        handleCancel={() => { setFileDeleted(null) }}
      />
    </>
  )

}
export const DocumentListingData = ({ index, item, fileTypeData, t, handleEditFile, handlePdfView, downloadFile, prescriptionsModuleEnabled, filetypeOxygenData, buttonStates, setDeletedFileName, setFileDeleted, safetyAssessmentData }) => {
  return (
    <ListItem divider disablePadding sx={{ py: 4 }}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        <StatusName item={item} />
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }}>
          <Stack spacing={1}>
            {prescriptionsModuleEnabled && filetypeOxygenData?.length > 0 && buttonStates && item.order_id && (
              <ItemLabel label={t('Dashboard.orderID')}>
                  <MuiLink component={RouterLink} to={`/orders/order-details/${item.order_id}`}>
                      {item.order_id}
                  </MuiLink>
              </ItemLabel>
            )}
            {item?.is_o2_prescription && <ItemLabel label={t('Prescriptions.fileType')} text={FileType[item?.is_o2_prescription]} />}
            {item?.formatted_expiration_date && <ItemLabel label={t('Prescriptions.expiration')} text={formatExpirationDate(item?.formatted_expiration_date)} />}
            {item?.rejection_reason && <ItemLabel label={t('Prescriptions.providerComment')} text={item?.rejection_reason} />}
            {item?.formatted_created_date && <ItemLabel label={t('Prescriptions.uploadedOn')} text={item?.formatted_created_date} />}
            {item?.created_by_user && <ItemLabel label={t('Prescriptions.uploadedBy')} text={item?.created_by_user} />}
            {item?.formatted_last_updated_date && item?.formatted_created_date !== item?.formatted_last_updated_date && (
              <ItemLabel label={t('Prescriptions.updatedOn')} text={item?.formatted_last_updated_date} />
            )}
            {item?.last_updated_by_user && item?.formatted_created_date !== item?.formatted_last_updated_date && (
              <ItemLabel label={t('Prescriptions.updatedBy')} text={item?.last_updated_by_user} />
            )}
            {item?.formatted_rejected_on && <ItemLabel label={t('Prescriptions.rejectedOn')} text={item?.formatted_rejected_on} />}
            {item?.rejected_by && <ItemLabel label={t('Prescriptions.rejectedBy')} text={item?.rejected_by} />}
          </Stack>
          <PrescriptionListingActions handlePdfView={handlePdfView} handleEditFile={handleEditFile} downloadFile={downloadFile} setFileDeleted={setFileDeleted} setDeletedFileName={setDeletedFileName} item={item} />
        </Stack>
      </Stack>
    </ListItem>
  );
}
export const EnvironmentalSafetyAssessment = ({ index, item, fileTypeData, t, patientDetailsName = "", noTopRadius = false }) => {
  const navigate = useNavigate();
  const handleSafetyAssessmentPdfView = (item) => {
    const navigationState = {
      order_id: item?.order_id,
      patient_id: item?.patient_id,
      patientDetailsName: patientDetailsName
    };
    if (location?.pathname?.includes('order')) {
      navigate(`/orders/environmental-safety-assessment-pdf/${item.order_id}`, { state: navigationState });
    } else {
      navigate(`/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/environmental-safety-assessment-pdf/${item.patient_id}`, { state: navigationState })
    }
  }
  return (
    <ListItem divider disablePadding sx={{ py: 4 }}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Typography variant="h6">{t('Prescriptions.environmentalSafetyAssessment')}</Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }}>
          <Stack spacing={1}>
            {item.order_id && (
              <ItemLabel label={t('Dashboard.orderID')}>
                <MuiLink component={RouterLink} to={`/orders/order-details/${item.order_id}`}>{item.order_id}</MuiLink>
              </ItemLabel>
            )}
            {item?.formatted_created_date && <ItemLabel label={t('Prescriptions.completedOn')} text={item?.formatted_created_date} />}
          </Stack>
          <Tooltip title={<span>{t("Prescriptions.viewDocument")}</span>}>
            <IconButton
              color="success"
              className="marginLeftEight"
              data-testid="ViewDocument"
              onClick={() => handleSafetyAssessmentPdfView(item)}
              onContextMenu={() => { handleSafetyAssessmentPdfView(item) }}
            >
              <Icon icon="fa-eye" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </ListItem>
  );
}

export const SafeHomeOxygenTherapy = ({ index, item, fileTypeData, t, patientDetailsName = ""}) => {
  const navigate = useNavigate();
  const handleSafeHomeOxygenTherapyPdfView = (item) => {
    const navigationState = {
      order_id: item?.order_id,
      patient_id: item?.patient_id,
      patientDetailsName: patientDetailsName
    };
    if (location?.pathname?.includes('order')) {
      navigate(`/orders/safe-home-oxygen-therapy-pdf/${item.order_id}`, { state: navigationState });
    } else {
      navigate(`/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/safe-home-oxygen-therapy-pdf/${item.patient_id}`, { state: navigationState })
    }
  };
  return (
    <ListItem divider disablePadding sx={{ py: 4 }}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Typography variant="h6">{t('Prescriptions.safeHomeOxygenTherapy')}</Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }}>
          <Stack spacing={1}>
            {item.order_id && (
              <ItemLabel label={t('Dashboard.orderID')}>
                 <MuiLink component={RouterLink} to={`/orders/order-details/${item.order_id}`}>
                     {item.order_id}
                 </MuiLink>
              </ItemLabel>
            )}
            {item?.formatted_created_date && <ItemLabel label={t('Prescriptions.completedOn')} text={item?.formatted_created_date} />}
          </Stack>
          <Tooltip title={<span>{t("Prescriptions.viewDocument")}</span>}>
            <IconButton
              color="success"
              className="marginLeftEight"
              data-testid="ViewDocument"
              onClick={() => handleSafeHomeOxygenTherapyPdfView(item)}
              onContextMenu={() => { handleSafeHomeOxygenTherapyPdfView(item) }}
            >
              <Icon icon="fa-eye" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </ListItem>
  );
}

export const PatientAcknowledgement = ({ index, item, fileTypeData, t, patientDetailsName = ""}) => {
  const navigate = useNavigate();
  const handlePatientAcknowledgementPdfView = (item) => {
    const navigationState = {
      order_id: item?.order_id,
      patient_id: item?.patient_id,
      patientDetailsName: patientDetailsName
    };
    if (location?.pathname?.includes('order')) {
      navigate(`/orders/patient-acknowledgement-pdf/${item.order_id}`, { state: navigationState });
    } else {
      navigate(`/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/patient-acknowledgement-pdf/${item.patient_id}`, { state: navigationState })
    }
  };
  return (
    <ListItem divider disablePadding sx={{ py: 4 }}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Typography variant="h6">{t('Prescriptions.patientAcknowledgement').replace(/patient/gi, LineOfBusinessEnum.patientToMember)}</Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'flex-start' }}>
          <Stack spacing={1}>
            {item.order_id && (
              <ItemLabel label={t('Dashboard.orderID')}>
                <MuiLink component={RouterLink} to={`/orders/order-details/${item.order_id}`}>
                    {item.order_id}
                </MuiLink>
              </ItemLabel>
            )}
            {item?.formatted_created_date && <ItemLabel label={t('Prescriptions.completedOn')} text={item?.formatted_created_date} />}
          </Stack>
          <Tooltip title={<span>{t("Prescriptions.viewDocument")}</span>}>
            <IconButton
              color="success"
              className="marginLeftEight"
              data-testid="ViewDocument"
              onClick={() => handlePatientAcknowledgementPdfView(item)}
              onContextMenu={() => { handlePatientAcknowledgementPdfView(item) }}
            >
              <Icon icon="fa-eye" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </ListItem>
  );
}

const PrescriptionListing = ({ patientDetailsName, externalDropZoneOpen, onExternalDropZoneClose }) => {
  const location = useLocation()
  const [isLoading, setLoading] = useState(true);
  const [isEditDocumentOpen, setIsEditDocumentOpen] = useState(false)
  const { t } = useTranslation();
  const [prescriptionListingData, setPrescriptionListingData] = useState([]);
  const [filetypeOxygenData, setFileTypeOxygenData] = useState([]);
  const [fileTypeOtherFileData, setFileTypeOtherFileData] = useState([]);
  const [isDropZoneOpen, setDropZonePhoneOpen] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [fileDeleted, setFileDeleted] = useState(null);
  const [deletedFileName, setDeletedFileName] = useState('');
  const [prescriptionFileId, setPrescriptionFileId] = useState('')
  const [isEdited, setIsEdited] = useState(false)
  const [prescriptionsModuleEnabled, setPrescriptionsModuleEnabled] = useState(false);
  const [addPrescriptionSettings, setAddPrescriptionSettings] = useState({});
  const [safetyAssessmentData, setSafetyAssessmentData] = useState([]);
  const [safeHomeOxygenTherapyData, setSafeHomeOxygenTherapyData] = useState([]);
  const [patientAcknowledgementData, setPatientAcknowledgementData] = useState([]);
  const isUrlOrderApprovalDetails = location.pathname.includes('approvals')
  const showMessage = location.pathname.includes('order') ?
    t('Prescriptions.noDocumentMessageOrder') : t('Prescriptions.noDocumentMessage').replace('#patient', LineOfBusinessEnum.patientToMember);

  const [buttonStates, setButtonStates] = useState({
    oxygen: true,
    referral: true,
    authorization: true,
    planOfCare: true,
    otherPrescription: true,
    otherFile: true,
    safetyAssessment: true,
    safeHomeOxygenTherapy: true,
    patientAcknowledgement: true
  });
  const handleButtonClick = (buttonName) => {
    setButtonStates((prevState) => ({
      ...prevState,
      [buttonName]: !prevState[buttonName],
    }));
  };

  const handleFileExpireTime = (id) => {
    const fileData = JSON.parse(localStorage?.getItem(`DownloadedFileData${id}`)) ?? {};
    if (fileData?.url && fileData?.id == id) {
      const url = new URL(fileData?.url)
      const expiresParam = url.searchParams.get('Expires');
      const expireDate = new Date(expiresParam * 1000)
      const currentDate = (new Date())
      if (expireDate - currentDate > 0) {
        return true
      } else {
        localStorage.removeItem(`DownloadedFileData${id}`);
        return false
      }
    }
    else {
      return false
    }
  }

  const handlePdfView = async (item) => {
    const pdfAPIResponseView = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", item.file_id));
    if (pdfAPIResponseView && pdfAPIResponseView.status_code == 200) {
      const pdfUrl = pdfAPIResponseView.data.url;
      window.open(pdfUrl);
    }
  }

  const downloadFile = async (item) => {
    const expireValue = handleFileExpireTime(item.file_id)
    let downloadUrl = '';
    let downloadFileName = ''

    if (expireValue) {
      const expireUrlId = JSON.parse(localStorage.getItem(`DownloadedFileData${item.file_id}`))
      downloadUrl = expireUrlId?.url;
      downloadFileName = expireUrlId?.fileName;
    }
    else {
      const pdfAPIResponseView = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", item.file_id));
      if (pdfAPIResponseView && pdfAPIResponseView.status_code == 200) {
        const pdfDownloadUrl = pdfAPIResponseView.data.url;

        localStorage.setItem(`DownloadedFileData${item.file_id}`, JSON.stringify({ url: pdfDownloadUrl, id: item.file_id, fileName: pdfAPIResponseView.data.file_name }))

        downloadUrl = pdfDownloadUrl;
        downloadFileName = pdfAPIResponseView.data.file_name
      }
    }
    fetch(downloadUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch((error) => {
        console.error('Error downloading file:', error);
      });
  };

  const handleFileRemove = async (e) => {
    e.preventDefault();
    const response = await PrescriptionListingApiCall(HTTPMethod.Post, PrescriptionApi.removeFile.replace("{file_upload_id}", fileDeleted?.file_id));

    if (response && response.status_code == 200 && response.data) {
      handlePrescriptionListing();
      handleFilePermission();
      successToast(t("Prescriptions.documentRemovedSuccessfully"));
    }
    setFileDeleted(null);
  }

  const handleEditFile = (item) => {
    setPrescriptionFileId(item);
    setIsEditDocumentOpen(true)
  }
  const handleDropZone = () => {
    setDropZonePhoneOpen(true);
  };
  useEffect(() => {
    if (externalDropZoneOpen) setDropZonePhoneOpen(true);
  }, [externalDropZoneOpen]);
  const handlePrescriptionListing = async () => {
    const orderId = location?.pathname?.split("/").pop();
    const patientId = location?.pathname?.split("/").pop();
    const requestBody = {
      order_id: location?.pathname?.includes('order') ? orderId : null,
      patient_id: /patients|members|residents/.test(location?.pathname) ? patientId : null
    };
    const response = await PrescriptionListingApiCall(
      HTTPMethod.Post,
      PrescriptionApi.prescriptionListing,
      requestBody
    );
    if (response && response.status_code == 200 && response.data) {
      setPrescriptionListingData(response?.data?.files);
      setFileTypeOxygenData(response?.data?.files.filter((file) => file.is_o2_prescription === true && file.is_esa !== 1 && file.file_type !== 5 && file.file_type !== 6));
      setFileTypeOtherFileData(response?.data?.files.filter((file) => file.is_o2_prescription === false && file.is_esa !== 1 && file.file_type !== 5 && file.file_type !== 6));
      setSafetyAssessmentData(response?.data?.files?.filter((file) => file.is_esa === 1));
      setSafeHomeOxygenTherapyData(response?.data?.files?.filter((file) => file.file_type === 5));
      setPatientAcknowledgementData(response?.data?.files?.filter((file) => file.file_type === 6));
    } else {
      setPrescriptionListingData([]);
    }
  };
  const handleFilePermission = async () => {
    const orderId = location?.pathname?.split("/").pop();
    const companyId = location?.pathname?.includes('order') ? FileUploadComponent.Order : FileUploadComponent.Patient;
    const response = await PrescriptionListingApiCall(HTTPMethod.Get, PrescriptionApi.getFileUploadPermission + "?component=" + companyId + "&component_id=" + orderId)
    if (response && response.status_code == 200 && response.data) {
      setPrescriptionsModuleEnabled(response.data?.file_upload_access_model?.o2_prescriptions_module_enabled)
      setAddPrescriptionSettings(response.data?.file_upload_access_model);
    }
  }
  useEffect(() => {
    setLoading(true);
    Promise.all([handlePrescriptionListing(), handleFilePermission()]).then(() => {
      setLoading(false);
    });
  }, [isEdited]);


  return (
    <div data-testid="PrescriptionListing">
      <Stack sx={{ gap: 4 }}>
        <CssBaseline />
        <Container className={styles.paddingContainer}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 5 }}>
            {prescriptionsModuleEnabled && prescriptionListingData?.length > 0 &&
              ((filetypeOxygenData?.length > 0 && prescriptionsModuleEnabled) ||
                fileTypeOtherFileData?.length > 0 ||
                safetyAssessmentData?.length > 0 || safeHomeOxygenTherapyData?.length > 0 || patientAcknowledgementData?.length > 0) && (
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  <FilterButton
                    data-testid="oxygen"
                    size="small"
                    selected={buttonStates.oxygen}
                    onClick={() => handleButtonClick("oxygen")}
                    label={t("Prescriptions.oxygenPrescription")}
                  />
                  <FilterButton
                    data-testid="otherFile"
                    size="small"
                    selected={buttonStates.otherFile}
                    onClick={() => handleButtonClick("otherFile")}
                    label={t("Prescriptions.otherFile")}
                  />
                  {!isUrlOrderApprovalDetails && (
                    <FilterButton
                      data-testid="safetyAssessment"
                      size="small"
                      selected={buttonStates.safetyAssessment}
                      onClick={() => handleButtonClick("safetyAssessment")}
                      label={t("Prescriptions.safetyAssessment")}
                    />
                  )}
                  {!isUrlOrderApprovalDetails && safeHomeOxygenTherapyData?.length > 0 && (
                    <FilterButton
                      data-testid="safeHomeOxygenTherapy"
                      size="small"
                      selected={buttonStates.safeHomeOxygenTherapy}
                      onClick={() => handleButtonClick("safeHomeOxygenTherapy")}
                      label={t("Prescriptions.safeHomeOxygenTherapy")}
                    />
                  )}
                  {!isUrlOrderApprovalDetails && patientAcknowledgementData?.length > 0 && (
                    <FilterButton
                      data-testid="patientAcknowledgement"
                      size="small"
                      selected={buttonStates.patientAcknowledgement}
                      onClick={() => handleButtonClick("patientAcknowledgement")}
                      label={t("Prescriptions.patientAcknowledgement").replace(/patient/gi, LineOfBusinessEnum.patientToMember)}
                    />
                  )}
                </Stack>
              )}
            <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ ml: 'auto' }}>
              <Button
                sx={{ display: { xs: 'none', md: 'flex' } }}
                color='primary'
                startIcon={<Icon icon="fa-plus" />}
                data-testid="addButton"
                onClick={handleDropZone}
              >
                {t("Prescriptions.addNew")}
              </Button>
              <AddPrescription
                addPrescriptionSettings={addPrescriptionSettings}
                prescriptionsModuleEnabled={prescriptionsModuleEnabled}
                isOpen={isDropZoneOpen}
                onClose={() => {
                  setDropZonePhoneOpen(false);
                  onExternalDropZoneClose?.();
                  handlePrescriptionListing();
                  handleFilePermission();
                }}
                setFileData={setFileData}
                fileData={fileData}
              />
            </Stack>
          </Stack>
          {isLoading ? (
            <PrescriptionListSkeleton />
          ) : prescriptionListingData?.length > 0 &&
            ((filetypeOxygenData?.length > 0 && prescriptionsModuleEnabled) ||
              fileTypeOtherFileData?.length > 0 || safetyAssessmentData?.length > 0 || safeHomeOxygenTherapyData?.length > 0 || patientAcknowledgementData?.length > 0) ? (
            <Stack spacing={6}>
              {prescriptionsModuleEnabled && buttonStates.oxygen && filetypeOxygenData?.length > 0 && (
                <Panel title={t("Prescriptions.oxygenPrescription")} expandable>
                  <List disablePadding>
                    {filetypeOxygenData?.map((item, index) => (
                      <DocumentListingData
                        item={item}
                        key={item?.file_id}
                        prescriptionsModuleEnabled={prescriptionsModuleEnabled}
                        buttonStates={buttonStates.oxygen}
                        filetypeOxygenData={filetypeOxygenData}
                        index={index}
                        fileTypeData={filetypeOxygenData}
                        t={t}
                        handleEditFile={handleEditFile}
                        handlePdfView={handlePdfView}
                        downloadFile={downloadFile}
                        handleFileRemove={handleFileRemove}
                        setFileDeleted={setFileDeleted}
                        setDeletedFileName={setDeletedFileName}
                      />
                    ))}
                  </List>
                </Panel>
              )}
              {buttonStates.otherFile && fileTypeOtherFileData?.length > 0 && (
                <Panel title={t("Prescriptions.otherFile")} expandable>
                  <List disablePadding>
                    {fileTypeOtherFileData?.map((item, index) => (
                      <DocumentListingData
                        item={item}
                        index={index}
                        fileTypeData={fileTypeOtherFileData}
                        t={t}
                        key={item?.file_id}
                        handleEditFile={handleEditFile}
                        handlePdfView={handlePdfView}
                        downloadFile={downloadFile}
                        handleFileRemove={handleFileRemove}
                        setFileDeleted={setFileDeleted}
                        setDeletedFileName={setDeletedFileName}
                      />
                    ))}
                  </List>
                </Panel>
              )}
              {buttonStates.safetyAssessment && !isUrlOrderApprovalDetails && safetyAssessmentData?.length > 0 && (
                <Panel title={t("Prescriptions.safetyAssessment")} expandable>
                  <List disablePadding>
                    {safetyAssessmentData?.map((item, index) => (
                      <EnvironmentalSafetyAssessment
                        item={item}
                        index={index}
                        fileTypeData={safetyAssessmentData}
                        t={t}
                        key={item?.file_id}
                        patientDetailsName={patientDetailsName}
                      />
                    ))}
                  </List>
                </Panel>
              )}
              {buttonStates.safeHomeOxygenTherapy && !isUrlOrderApprovalDetails && safeHomeOxygenTherapyData?.length > 0 && (
                <Panel title={t("Prescriptions.safeHomeOxygenTherapy")} expandable>
                  <List disablePadding>
                    {safeHomeOxygenTherapyData?.map((item, index) => (
                      <SafeHomeOxygenTherapy
                        item={item}
                        index={index}
                        fileTypeData={safeHomeOxygenTherapyData}
                        t={t}
                        key={item?.file_id}
                        patientDetailsName={patientDetailsName}
                      />
                    ))}
                  </List>
                </Panel>
              )}
              {buttonStates.patientAcknowledgement && !isUrlOrderApprovalDetails && patientAcknowledgementData?.length > 0 && (
                <Panel title={t("Prescriptions.patientAcknowledgement").replace(/patient/gi, LineOfBusinessEnum.patientToMember)} expandable>
                  <List disablePadding>
                    {patientAcknowledgementData?.map((item, index) => (
                      <PatientAcknowledgement
                        item={item}
                        index={index}
                        fileTypeData={patientAcknowledgementData}
                        t={t}
                        key={item?.file_id}
                        patientDetailsName={patientDetailsName}
                      />
                    ))}
                  </List>
                </Panel>
              )}
              {!((buttonStates.oxygen && filetypeOxygenData?.length > 0) ||
                (buttonStates.otherFile && fileTypeOtherFileData?.length > 0) ||
                (buttonStates.safetyAssessment && safetyAssessmentData?.length > 0) ||
                (buttonStates.safeHomeOxygenTherapy && safeHomeOxygenTherapyData?.length > 0) ||
                (buttonStates.patientAcknowledgement && patientAcknowledgementData?.length > 0)) && (
                  <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                    <NoData message={"There is no document in result."} />
                  </Stack>
                )}
            </Stack>
          ) : (
            <Card>
              <NoData message={showMessage} />
            </Card>
          )}

        </Container>
        {isEditDocumentOpen && (
          <EditPrescription
            isEditDocumentOpen={isEditDocumentOpen}
            currentListData={prescriptionFileId}
            setIsEdited={setIsEdited}
            addPrescriptionSettings={
              addPrescriptionSettings.o2_prescriptions_module_enabled
            }
            handleClose={() => {
              setIsEditDocumentOpen(false);
              handlePrescriptionListing();
              handleFilePermission();
            }}
          >
            {" "}
          </EditPrescription>
        )}
      </Stack>

      <CommonAlert
        contentText={deletedFileName}
        headerText="Remove Document"
        okText="Proceed"
        cancelText="Cancel"
        isOpen={!!fileDeleted}
        handleSubmit={handleFileRemove}
        okButtonStyle={"red"}
        handleCancel={() => {
          setFileDeleted(null);
        }}
      />
    </div>
  );
};
export default PrescriptionListing;
